import _ from "underscore";
import $ from "jquery";
import store from "globals/store";
import Component from "models/component";
import CodePrototype from "models/prototypes/code";
import makeAbsoluteUrl from "utils/make-absolute-url";
import getCodeMirrorLanguage from "utils/get-code-mirror-language";
import CodeMirror from "codemirror";
import "utils/codemirror-runmode";
import "utils/codemirror-mode-inline-editor";
import { _translateInlineAlways } from "utils/localisation";
import { console as codeConsole } from "globals/console";
import { hasTaxonomy } from "utils/taxonomy.js";
import { task } from "globals/task";

export default store["models/html"] = Component.extend(CodePrototype).extend({
  defaults: function () {
    return _.extend({}, CodePrototype.defaults, {
      autoRun: true,
      autoTest: false,
    });
  },

  initialize: function (options) {
    CodePrototype.initialize.apply(this, [options]);
    Component.prototype.initialize.apply(this, [options]);
  },

  //runs the html code
  run: function (options) {
    this.trigger("run", options);
    if (
      hasTaxonomy(task, "task-type.build") ||
      hasTaxonomy(task, "use-type.free-code")
    ) {
      codeConsole.clear();
    }
    this.validateCode();
    this.trigger("post-run", options);
  },

  parse: function (response) {
    if (response.makeUrlsRelative && response.raw) {
      //replace src & href
      response.raw = response.raw.replace(
        /((?:src|href)\s*=\s*('|"))(.*)\2/gi,
        function (fullMatch, firstBit, quote, url) {
          return firstBit + makeAbsoluteUrl(url, true) + quote;
        },
      );

      //replace css background-image
      response.raw = response.raw.replace(
        /(url\s*\(\s*("|'))(.*)\2\s*\)/gi,
        function (fullMatch, firstBit, quote, url) {
          return firstBit + makeAbsoluteUrl(url, true) + quote;
        },
      );
    }

    delete response.makeUrlsRelative;

    return CodePrototype.parse.apply(this, [response]);
  },

  export: function (options) {
    var output = CodePrototype.export.apply(this, [options]);

    //make URLs secure for inline frame
    var urlFixup = function (url) {
      return url.replace(/^http:/, "https:");
    };

    //make paths absolute for export
    if (options.absolutePaths) {
      urlFixup = makeAbsoluteUrl;
    }

    //replace src & href
    output = output.replace(
      /((?:src|href)\s*=\s*('|"))(.*)\2/gi,
      function (fullMatch, firstBit, quote, url) {
        return firstBit + urlFixup(url) + quote;
      },
    );

    //replace css background-image
    output = output.replace(
      /(url\s*\(\s*("|'))(.*)(\2\s*\))/gi,
      function (fullMatch, firstBit, quote, url, lastBit) {
        return firstBit + urlFixup(url) + lastBit;
      },
    );

    return output;
  },

  validateCode: function () {
    var config = _.defaults(
      {
        mode: "inline-editor",
        baseMode: {
          mode: getCodeMirrorLanguage(this),
        },
      },
      CodeMirror.defaults,
    );
    var eventError = null;
    CodeMirror.runMode(
      this.exportRaw(),
      CodeMirror.getMode(config, config.mode),
      function (current, style, lineIndex, chIndex) {
        if (eventError === null && style && style.indexOf(" error") !== -1) {
          eventError = {
            message: style,
            line: lineIndex,
            character: chIndex,
          };
        }
      },
    );

    if (eventError) {
      this.set("syntax-error", eventError);
      this.set("code-error", eventError);
    } else {
      this.unset("syntax-error");
      this.unset("code-error");
    }

    if (eventError !== null) {
      this.trigger("code-error", eventError);
    }
  },

  tests: _.extend({}, CodePrototype.tests, {
    //tests whether the code is valid
    "code-valid": function (value) {
      if (!value || !this.get("code-error")) {
        return Promise.resolve();
      } else {
        return Promise.Promise.reject();
      }
    },

    //runs a jquery selector on the source and returns true if a match is found
    jquery: function (options) {
      if (!options || !options.selector || !options.operation) {
        return Promise.resolve();
      }

      //we surround the document with a <section> so that $.find can look into it
      //this means we can't test anything outside the <body> tag
      var element = $("<section>" + this.exportRaw() + "</section>").find(
        String(options.selector),
      );

      //limit by index
      if (typeof options.index === "number") {
        element = element.eq(options.index);

        //we have to support indexes of type string because some tasks have been
        // saved with this data before it was changed to be a number
      } else if (typeof options.index === "string" && options.index) {
        element = element.eq(Number(options.index));
      }

      switch (options.operation) {
        //existence test
        case "exists":
          if (element.length) {
            return Promise.resolve();
          } else {
            return Promise.reject();
          }

        //text content test
        case "text-content":
          // Remove all child elements so we only get the text of the directly-
          // selected element itself. E.g. <div>Hello <span>World</span></div>
          // selecting 'div' will return 'Hello '.
          var text = element.clone().children().remove().end().text() || "";

          if (
            _translateInlineAlways(text).toLowerCase() ===
            _translateInlineAlways(options.value).toLowerCase()
          ) {
            return Promise.resolve();
          } else {
            return Promise.reject();
          }

        //attribute test
        case "attribute":
          var value = element.attr(options.attribute) || "";

          if (value === _translateInlineAlways(options.value)) {
            return Promise.resolve();
          } else {
            return Promise.reject();
          }
      }

      //pass if operation is not recognized
      return Promise.resolve();
    },
  }),
});
